import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import i18n from 'i18next';
import moment from 'moment';
import {
  FORM_TIMING,
  VALIDATION,
  STORAGE,
  FORM_TYPES,
} from '../constants/formConstants';
import { getDraftById } from '../utils/draftStorage';
import { cleanLabel } from '../utils/validationUtils';
import {
  normalizeWorkflowLicenseMode,
  computeEffectiveHasHcpLicensesTab,
} from '../utils/workflowLicenseMode';
import { HCP_PROFILE_REQUEST_TAB_IDS } from '../constants/hcpProfileRequestConstants';

/** Calendar day in local TZ for date / ISO / Moment (Formik DatePicker). */
function toCalendarDayMs(d) {
  if (d == null || d === '') {
    return null;
  }
  if (moment.isMoment(d)) {
    const m = d.clone().startOf('day');
    return m.isValid() ? m.valueOf() : null;
  }
  if (d instanceof Date) {
    const m = moment(d).startOf('day');
    return m.isValid() ? m.valueOf() : null;
  }
  const m = moment(d).startOf('day');
  return m.isValid() ? m.valueOf() : null;
}

const trimStringValues = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return obj.trim();
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => trimStringValues(item));
  }

  if (typeof obj === 'object') {
    const trimmedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        trimmedObj[key] = trimStringValues(obj[key]);
      }
    }
    return trimmedObj;
  }

  return obj;
};

const coalesceHcpListEnabled = (raw) => {
  if (raw === true || raw === 1) {
    return true;
  }
  if (raw === false || raw === 0) {
    return false;
  }
  if (raw == null) {
    return false;
  }
  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (t === '1' || t === 'true' || t === 'yes') {
      return true;
    }
    if (t === '0' || t === 'false' || t === '' || t === 'no') {
      return false;
    }
  }
  return Boolean(raw);
};

/** Profile snapshot tabs apply only in licensing_tool mode. */
const effectiveAttachEndUserProfileSnapshot = (
  formFlag,
  licensingExperienceMode
) =>
  coalesceHcpListEnabled(formFlag) &&
  licensingExperienceMode === 'licensing_tool';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function additionalNotificationDatesFromApiRow(hcpList) {
  if (!hcpList) {
    return '';
  }
  if (
    Array.isArray(hcpList.additional_notification_dates) &&
    hcpList.additional_notification_dates.length
  ) {
    return hcpList.additional_notification_dates.join(', ');
  }
  const raw = hcpList.additional_notification_dates_json;
  if (raw && typeof raw === 'string') {
    try {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        const norm = [
          ...new Set(
            arr
              .map((s) => String(s).slice(0, 10))
              .filter((d) => ISO_DATE_RE.test(d))
          ),
        ].sort();
        return norm.join(', ');
      }
    } catch (_e) {
      /* ignore */
    }
  }
  return '';
}

/** Map API HCP row (+ nested files) to MultiForm `hcpLicensesList` shape. */
function normalizeHcpListPayloadFromApiRow(hcpList) {
  if (!hcpList) {
    return null;
  }
  const normalizeFileField = (fileData, defaultName) => {
    if (!fileData) {
      return null;
    }
    const file = Array.isArray(fileData) ? fileData[0] : fileData;
    if (!file) {
      return null;
    }
    return {
      id: file.id,
      name: file.original_name || file.name || defaultName,
      size: Number(file.file_size || file.size || 0),
      type: file.mime_type || file.type || 'application/octet-stream',
      original_name: file.original_name || file.name || defaultName,
      file_size: Number(file.file_size || file.size || 0),
      mime_type: file.mime_type || file.type || 'application/octet-stream',
    };
  };

  const licenseImage = hcpList.licenseImage
    ? normalizeFileField(hcpList.licenseImage, 'license-image')
    : null;
  const additionalList = Array.isArray(hcpList.additionalAttachmentFiles)
    ? hcpList.additionalAttachmentFiles
        .map((f) => normalizeFileField(f, 'attachment'))
        .filter(Boolean)
    : [];

  return {
    id: hcpList.id || null,
    hcp_name: hcpList.hcp_name || '',
    hcp_emirates_id: hcpList.hcp_emirates_id || '',
    hcp_email: hcpList.hcp_email || '',
    hcp_phone: hcpList.hcp_phone || '',
    license_number: hcpList.license_number || '',
    license_issue_date: hcpList.license_issue_date || null,
    license_end_date: hcpList.license_end_date || null,
    /** Snapshot from loaded license; used only for renewal date validation. */
    renewalBaselineIssueDate: hcpList.license_issue_date || null,
    renewalBaselineEndDate: hcpList.license_end_date || null,
    license_image_file: licenseImage ? [licenseImage] : [],
    total_cme_hours:
      hcpList.total_cme_hours === null || hcpList.total_cme_hours === undefined
        ? ''
        : String(hcpList.total_cme_hours),
    additional_attachments: additionalList,
    additional_notification_dates:
      additionalNotificationDatesFromApiRow(hcpList),
  };
}

const MultiFormContext = createContext();
const FORM_ACTIONS = {
  SET_FORM_DATA: 'SET_FORM_DATA',
  SET_FORM_ERRORS: 'SET_FORM_ERRORS',
  SET_FORM_LOADING: 'SET_FORM_LOADING',
  SET_SUBMISSION_STATE: 'SET_SUBMISSION_STATE',
  CLEAR_FORM_ERRORS: 'CLEAR_FORM_ERRORS',
  RESET_FORMS: 'RESET_FORMS',
  SET_FORM_VALIDITY: 'SET_FORM_VALIDITY',
  HYDRATE_FROM_STORAGE: 'HYDRATE_FROM_STORAGE',
  SET_SUB_FORMS: 'SET_SUB_FORMS',
  SET_REQUEST: 'SET_REQUEST',
  SET_FILLED_FORM: 'SET_FILLED_FORM',
  SET_HYDRATION_COMPLETE: 'SET_HYDRATION_COMPLETE',
  SET_API_DATA_LOADED: 'SET_API_DATA_LOADED',
  SET_INITIALIZATION_COMPLETE: 'SET_INITIALIZATION_COMPLETE',
  SET_FORM_NAME: 'SET_FORM_NAME',
  SET_WORKFLOW_NAME: 'SET_WORKFLOW_NAME',
  SET_ACTIVE_SUB_FORM_TAB: 'SET_ACTIVE_SUB_FORM_TAB',
  ADD_ADDITIONAL_DOCUMENT: 'ADD_ADDITIONAL_DOCUMENT',
  UPDATE_ADDITIONAL_DOCUMENT: 'UPDATE_ADDITIONAL_DOCUMENT',
  DELETE_ADDITIONAL_DOCUMENT: 'DELETE_ADDITIONAL_DOCUMENT',
  SET_ADDITIONAL_DOCUMENTS: 'SET_ADDITIONAL_DOCUMENTS',
  RESET_DATA_SOURCE: 'RESET_DATA_SOURCE',
};

const initialState = {
  formSections: {},
  hcpLicensesList: {
    hcp_name: '',
    hcp_emirates_id: '',
    hcp_email: '',
    hcp_phone: '',
    license_number: '',
    license_issue_date: null,
    license_end_date: null,
    renewalBaselineIssueDate: null,
    renewalBaselineEndDate: null,
    license_image_file: [],
    total_cme_hours: '',
    additional_attachments: [],
    additional_notification_dates: '',
  },
  additionalDocuments: [],
  subForms: [],
  request: null,
  filledForm: null,
  formName: '',
  workflowName: '',
  activeSubFormTab: null,
  hasHcpLicensesList: false,
  attachEndUserProfileSnapshot: false,
  isHydrationComplete: false,
  hasHydratedData: false,
  isApiDataLoaded: false,
  isInitializationComplete: false,
  errors: {
    formSections: {},
    hcpLicensesList: {},
  },
  loading: {
    formSections: false,
    hcpLicensesList: false,
  },
  validity: {
    formSections: false,
    hcpLicensesList: false,
  },
  submissionState: {
    isSubmitting: false,
    success: false,
    error: null,
    data: null,
  },
};

const multiFormReducer = (state, action) => {
  switch (action.type) {
    case FORM_ACTIONS.SET_FORM_DATA: {
      if (
        action.formType === 'hasHcpLicensesList' &&
        typeof action.data === 'boolean'
      ) {
        return { ...state, hasHcpLicensesList: action.data };
      }
      if (
        action.formType === 'attachEndUserProfileSnapshot' &&
        typeof action.data === 'boolean'
      ) {
        return { ...state, attachEndUserProfileSnapshot: action.data };
      }
      const newData = {
        ...state[action.formType],
        ...action.data,
      };
      return {
        ...state,
        [action.formType]: newData,
      };
    }

    case FORM_ACTIONS.SET_FORM_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.formType]: action.errors,
        },
      };

    case FORM_ACTIONS.SET_FORM_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.formType]: action.loading,
        },
      };

    case FORM_ACTIONS.SET_SUBMISSION_STATE:
      return {
        ...state,
        submissionState: {
          ...state.submissionState,
          ...action.state,
        },
      };

    case FORM_ACTIONS.CLEAR_FORM_ERRORS:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.formType]: {},
        },
      };

    case FORM_ACTIONS.SET_FORM_VALIDITY:
      return {
        ...state,
        validity: {
          ...state.validity,
          [action.formType]: action.valid,
        },
      };

    case FORM_ACTIONS.RESET_FORMS:
      return {
        ...initialState,
        formSections: state.formSections,
        additionalDocuments: state.additionalDocuments,
      };

    case FORM_ACTIONS.RESET_DATA_SOURCE:
      return {
        ...state,
        isApiDataLoaded: false,
        isInitializationComplete: false,
        isHydrationComplete: false,
        hasHydratedData: false,
        subForms: [],
        filledForm: null,
        formSections: {},
        hasHcpLicensesList: false,
        attachEndUserProfileSnapshot: false,
        hcpLicensesList: { ...initialState.hcpLicensesList },
        formName: '',
        workflowName: '',
        request: null,
        activeSubFormTab: null,
        errors: { ...initialState.errors },
        validity: { ...initialState.validity },
        submissionState: { ...initialState.submissionState },
      };

    case FORM_ACTIONS.HYDRATE_FROM_STORAGE:
      const hydratedState = {
        ...state,
        formSections: {
          ...state.formSections,
          ...action.data.formSections,
        },
        hcpLicensesList: {
          ...state.hcpLicensesList,
          ...action.data.hcpLicensesList,
        },
        additionalDocuments:
          action.data.additionalDocuments || state.additionalDocuments || [],
      };

      return hydratedState;

    case FORM_ACTIONS.SET_SUB_FORMS:
      return {
        ...state,
        subForms: action.subForms,
      };

    case FORM_ACTIONS.SET_REQUEST:
      return {
        ...state,
        request: action.request,
      };

    case FORM_ACTIONS.SET_FILLED_FORM:
      return {
        ...state,
        filledForm: action.filledForm,
      };

    case FORM_ACTIONS.SET_HYDRATION_COMPLETE:
      return {
        ...state,
        isHydrationComplete: true,
        hasHydratedData: action.hasData,
      };

    case FORM_ACTIONS.SET_API_DATA_LOADED:
      return {
        ...state,
        isApiDataLoaded: action.loaded,
      };

    case FORM_ACTIONS.SET_INITIALIZATION_COMPLETE:
      return {
        ...state,
        isInitializationComplete: action.complete,
      };

    case FORM_ACTIONS.SET_FORM_NAME:
      return {
        ...state,
        formName: action.formName,
      };

    case FORM_ACTIONS.SET_WORKFLOW_NAME:
      return {
        ...state,
        workflowName: action.workflowName,
      };

    case FORM_ACTIONS.SET_ACTIVE_SUB_FORM_TAB:
      return {
        ...state,
        activeSubFormTab: action.activeSubFormTab,
      };

    case FORM_ACTIONS.ADD_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: [action.document, ...state.additionalDocuments],
      };

    case FORM_ACTIONS.UPDATE_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: state.additionalDocuments.map((doc) =>
          doc.id === action.documentId ? { ...doc, ...action.updates } : doc
        ),
      };

    case FORM_ACTIONS.DELETE_ADDITIONAL_DOCUMENT:
      return {
        ...state,
        additionalDocuments: state.additionalDocuments.filter(
          (doc) => doc.id !== action.documentId
        ),
      };

    case FORM_ACTIONS.SET_ADDITIONAL_DOCUMENTS:
      return {
        ...state,
        additionalDocuments: action.documents,
      };

    default:
      return state;
  }
};

export const MultiFormProvider = ({
  children,
  requestId,
  stageId: initialStageId,
  workflowVersionId,
  studyName,
  requestProgressId,
  isSendbackMode = false,
  draftId,
  linkedStudiesEnabled = false,
  linkedStudiesRequired = false,
  linkedRequestIds = [],
  licensingExperienceMode = 'license_tracking',
}) => {
  const [state, dispatch] = useReducer(multiFormReducer, initialState);
  const licensingModeRef = useRef(licensingExperienceMode);

  useEffect(() => {
    licensingModeRef.current = licensingExperienceMode;
  }, [licensingExperienceMode]);
  const formSectionsDataRef = useRef({});
  const modePrefix = isSendbackMode ? 'sendBack_' : 'newRequest_';
  const oppositeModePrefix = isSendbackMode ? 'newRequest_' : 'sendBack_';

  // For new requests, use workflowVersionId; for sendback, use requestProgressId or requestId
  const dynamicId = isSendbackMode
    ? requestProgressId || requestId || STORAGE.DRAFT_KEY
    : workflowVersionId || STORAGE.DRAFT_KEY;

  const storageKey = `${STORAGE.MULTI_FORM_PREFIX}${modePrefix}${dynamicId}`;

  const hcpVerticalTabDefaultAppliedRef = useRef(false);
  const hcpProfileMenuDefaultAppliedRef = useRef(false);

  useEffect(() => {
    hcpVerticalTabDefaultAppliedRef.current = false;
    hcpProfileMenuDefaultAppliedRef.current = false;
  }, [dynamicId]);

  const dataSourceIdRef = useRef(undefined);
  const renewalFetchKeyRef = useRef('');
  /** Latest HCP slice for merging Formik updates (Formik omits renewal baselines). */
  const hcpLicensesListMergeRef = useRef(initialState.hcpLicensesList);

  useEffect(() => {
    hcpLicensesListMergeRef.current = state.hcpLicensesList;
  }, [state.hcpLicensesList]);

  useEffect(() => {
    const id = isSendbackMode
      ? String(requestProgressId ?? '')
      : String(workflowVersionId ?? '');
    if (dataSourceIdRef.current === undefined) {
      dataSourceIdRef.current = id;
      return;
    }
    if (dataSourceIdRef.current !== id) {
      dataSourceIdRef.current = id;
      formSectionsDataRef.current = {};
      dispatch({ type: FORM_ACTIONS.RESET_DATA_SOURCE });
    }
  }, [isSendbackMode, requestProgressId, workflowVersionId]);

  const setFormSectionsDataRef = useCallback((getDataFn) => {
    formSectionsDataRef.current = getDataFn;
  }, []);

  const getFormSectionsData = useCallback(() => {
    if (
      formSectionsDataRef.current &&
      typeof formSectionsDataRef.current === 'function'
    ) {
      return formSectionsDataRef.current() || {};
    }
    return {};
  }, []);

  const cleanupConflictingStorage = useCallback(() => {
    try {
      // Only clean up keys that could conflict with the current request
      const conflictingKey = `${STORAGE.MULTI_FORM_PREFIX}${oppositeModePrefix}${dynamicId}`;

      if (localStorage.getItem(conflictingKey)) {
        localStorage.removeItem(conflictingKey);
      }
    } catch (error) {
      // Silent error handling
    }
  }, [oppositeModePrefix, dynamicId]);

  const performInitializationIfNeeded = useCallback(
    (subForms = []) => {
      if (
        state.isHydrationComplete &&
        state.isApiDataLoaded &&
        !state.isInitializationComplete
      ) {
        const currentHasData = Object.keys(state.formSections).some(
          (formId) => {
            const formData = state.formSections[formId];
            return (
              formData &&
              Object.values(formData).some((val) => val && val.trim() !== '')
            );
          }
        );

        if (!state.hasHydratedData && !currentHasData && subForms.length > 0) {
          const initialFormData = {};
          subForms.forEach((sf) => {
            initialFormData[sf.id] = {};
          });

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: 'formSections',
            data: initialFormData,
          });
        }

        dispatch({
          type: FORM_ACTIONS.SET_INITIALIZATION_COMPLETE,
          complete: true,
        });
      }
    },
    [
      state.isHydrationComplete,
      state.hasHydratedData,
      state.isApiDataLoaded,
      state.isInitializationComplete,
      state.formSections,
    ]
  );

  const fetchWorkflowDataAndInitialize = useCallback(
    async (workflowVersionId) => {
      if (!workflowVersionId || state.isApiDataLoaded) {
        return;
      }

      try {
        const response = await axiosInstance.get(
          `/api/workflows/endUser/version/${workflowVersionId}/submission-stages`
        );
        const body = response.data || {};
        const stages = body.stages || [];
        const firstStage = stages[0];
        const forms =
          firstStage?.Forms?.length > 0
            ? firstStage.Forms
            : firstStage?.Form
              ? [firstStage.Form]
              : [];
        const formData = forms[0] || null;
        const fetchedSubForms = forms.flatMap((f) => f?.subforms || []);

        const workflowMode = normalizeWorkflowLicenseMode(
          body.workflow_license_mode,
          body.license_renewal
        );
        const entryFormClaimsHcp = coalesceHcpListEnabled(
          formData &&
            Object.prototype.hasOwnProperty.call(
              formData,
              'has_hcp_licenses_list'
            )
            ? formData.has_hcp_licenses_list
            : false
        );
        const effectiveHasHcp = computeEffectiveHasHcpLicensesTab({
          workflowLicenseMode: workflowMode,
          licenseRenewalFallback: body.license_renewal,
          anySubmissionFormHasHcpLicenses:
            body.anySubmissionFormHasHcpLicenses === true,
          entryFormHasHcpFlag: entryFormClaimsHcp,
          hcpListRowPresent: false,
        });

        dispatch({
          type: FORM_ACTIONS.SET_WORKFLOW_NAME,
          workflowName: body.workflowVersionName || '',
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_NAME,
          formName: formData?.name || '',
        });

        dispatch({
          type: FORM_ACTIONS.SET_SUB_FORMS,
          subForms: fetchedSubForms,
        });

        if (formData?.id != null) {
          dispatch({
            type: FORM_ACTIONS.SET_FILLED_FORM,
            filledForm: {
              id: null,
              form_id: formData.id,
              value: '{}',
            },
          });
        }

        dispatch({
          type: FORM_ACTIONS.SET_REQUEST,
          request: {
            id: body.workflowVersionId ?? workflowVersionId,
            name: body.workflowVersionName,
            license_renewal: body.license_renewal,
            workflow_license_mode: workflowMode,
            Stages: stages,
          },
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: 'hasHcpLicensesList',
          data: effectiveHasHcp,
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: 'attachEndUserProfileSnapshot',
          data: effectiveAttachEndUserProfileSnapshot(
            formData &&
              Object.prototype.hasOwnProperty.call(
                formData,
                'attach_end_user_profile_snapshot'
              )
              ? formData.attach_end_user_profile_snapshot
              : false,
            licensingModeRef.current
          ),
        });

        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: true,
        });

        setTimeout(() => {
          performInitializationIfNeeded(fetchedSubForms);
        }, FORM_TIMING.SYNC_DELAY_MS);
      } catch (error) {
        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: false,
        });
      }
    },
    [state.isApiDataLoaded, performInitializationIfNeeded]
  );

  useEffect(() => {
    renewalFetchKeyRef.current = '';
  }, [workflowVersionId]);

  useEffect(() => {
    if (isSendbackMode) {
      return;
    }
    const wfMode = normalizeWorkflowLicenseMode(
      state.request?.workflow_license_mode,
      state.request?.license_renewal
    );
    if (wfMode === 'normal') {
      return;
    }
    if (
      !state.isApiDataLoaded ||
      !state.hasHcpLicensesList ||
      !workflowVersionId
    ) {
      return;
    }
    if (!coalesceHcpListEnabled(state.request?.license_renewal)) {
      return;
    }
    const digits = (studyName || '').replace(/\D/g, '');
    if (!/^\d{15}$/.test(digits)) {
      return;
    }
    const key = `${workflowVersionId}|${digits}`;
    if (renewalFetchKeyRef.current === key) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const seedRes = await axiosInstance.get(
          '/api/hcp-licenses-list/enduser/renewal-seed',
          {
            params: { emirates_id: digits },
            suppressErrorToast: true,
          }
        );
        if (cancelled) {
          return;
        }
        const payload = normalizeHcpListPayloadFromApiRow(seedRes.data);
        if (payload) {
          renewalFetchKeyRef.current = key;
          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: 'hcpLicensesList',
            data: payload,
          });
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.message ||
            i18n.t('Emirates ID no license to renew');
          dispatch({
            type: FORM_ACTIONS.SET_FORM_ERRORS,
            formType: 'hcpLicensesList',
            errors: {
              fields: {},
              hasErrors: true,
              general: msg,
            },
          });
          dispatch({
            type: FORM_ACTIONS.SET_FORM_VALIDITY,
            formType: 'hcpLicensesList',
            valid: false,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isSendbackMode,
    state.isApiDataLoaded,
    state.hasHcpLicensesList,
    state.request?.license_renewal,
    state.request?.workflow_license_mode,
    studyName,
    workflowVersionId,
  ]);

  const fetchSendbackDataAndInitialize = useCallback(
    async (requestProgressId) => {
      if (!requestProgressId || state.isApiDataLoaded) {
        return;
      }

      try {
        const response = await axiosInstance.get(
          `/api/requests/enduser/details/${requestProgressId}`
        );
        const data = response.data;

        // formSections: merged after we know the full subform list (see sub_forms_info block below)

        // load License Information if available
        if (data.hcpLicensesList) {
          const normalizeFileField = (fileData, defaultName) => {
            if (!fileData) {
              return null;
            }

            const file = Array.isArray(fileData) ? fileData[0] : fileData;
            if (!file) {
              return null;
            }

            const normalized = {
              id: file.id,
              name: file.original_name || file.name || defaultName,
              size: Number(file.file_size || file.size || 0),
              type: file.mime_type || file.type || 'application/octet-stream',
              original_name: file.original_name || file.name || defaultName,
              file_size: Number(file.file_size || file.size || 0),
              mime_type:
                file.mime_type || file.type || 'application/octet-stream',
            };

            return normalized;
          };

          const hcpList = data.hcpLicensesList;
          const licenseImage = hcpList.licenseImage
            ? normalizeFileField(hcpList.licenseImage, 'license-image')
            : null;
          const additionalList = Array.isArray(
            hcpList.additionalAttachmentFiles
          )
            ? hcpList.additionalAttachmentFiles
                .map((f) => normalizeFileField(f, 'attachment'))
                .filter(Boolean)
            : [];

          const hcpLicensesListData = {
            id: hcpList.id || null,
            hcp_name: hcpList.hcp_name || '',
            hcp_emirates_id: hcpList.hcp_emirates_id || '',
            hcp_email: hcpList.hcp_email || '',
            hcp_phone: hcpList.hcp_phone || '',
            license_number: hcpList.license_number || '',
            license_issue_date: hcpList.license_issue_date || null,
            license_end_date: hcpList.license_end_date || null,
            license_image_file: licenseImage ? [licenseImage] : [],
            total_cme_hours:
              hcpList.total_cme_hours === null ||
              hcpList.total_cme_hours === undefined
                ? ''
                : String(hcpList.total_cme_hours),
            additional_attachments: additionalList,
            additional_notification_dates:
              additionalNotificationDatesFromApiRow(hcpList),
          };

          dispatch({
            type: FORM_ACTIONS.SET_FORM_DATA,
            formType: 'hcpLicensesList',
            data: hcpLicensesListData,
          });
        }

        const metaAll = data.workflowSubmissionLicenseMeta || {};
        const wfAll = data.request?.WorkflowVersion || data.request?.wf_version;
        const mergedLicenseMode = normalizeWorkflowLicenseMode(
          metaAll.workflow_license_mode ??
            wfAll?.workflow_license_mode ??
            data.request?.workflow_license_mode,
          metaAll.license_renewal ??
            wfAll?.license_renewal ??
            data.request?.license_renewal
        );

        if (data.request) {
          dispatch({
            type: FORM_ACTIONS.SET_REQUEST,
            request: {
              ...data.request,
              workflow_license_mode: mergedLicenseMode,
            },
          });

          // Get workflow name from workflow version, not form name
          const workflowName =
            data.request?.wf_version?.name ||
            data.request?.WorkflowVersion?.name ||
            data.order?.[0]?.form?.name;

          if (workflowName) {
            dispatch({
              type: FORM_ACTIONS.SET_WORKFLOW_NAME,
              workflowName: workflowName,
            });
          }

          // Get form name from the order or workflow version stages
          const formName =
            data.order?.[0]?.form?.name ||
            data.request?.wf_version?.Stages?.[0]?.Form?.name ||
            data.request?.WorkflowVersion?.Stages?.[0]?.Form?.name;

          if (formName) {
            dispatch({
              type: FORM_ACTIONS.SET_FORM_NAME,
              formName: formName,
            });
          }
        }

        const rowPresentHc =
          !!data.hcpLicensesList &&
          data.hcpLicensesList.id != null &&
          data.hcpLicensesList.id !== '';

        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: 'hasHcpLicensesList',
          data: computeEffectiveHasHcpLicensesTab({
            workflowLicenseMode: mergedLicenseMode,
            licenseRenewalFallback: wfAll?.license_renewal,
            anySubmissionFormHasHcpLicenses:
              metaAll.anySubmissionFormHasHcpLicenses === true,
            entryFormHasHcpFlag: !!data.form_has_hcp_licenses_list,
            hcpListRowPresent: rowPresentHc,
          }),
        });

        dispatch({
          type: FORM_ACTIONS.SET_FORM_DATA,
          formType: 'attachEndUserProfileSnapshot',
          data: effectiveAttachEndUserProfileSnapshot(
            data.form_attach_end_user_profile_snapshot,
            licensingModeRef.current
          ),
        });

        if (data.filled_form) {
          dispatch({
            type: FORM_ACTIONS.SET_FILLED_FORM,
            filledForm: data.filled_form,
          });
        }

        let subformsToSet = null;

        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: true,
        });

        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: true,
        });

        if (subformsToSet && subformsToSet.length > 0) {
          setTimeout(() => {
            performInitializationIfNeeded(subformsToSet);
          }, FORM_TIMING.SYNC_DELAY_MS);
        }

        if (
          !subformsToSet &&
          data.sub_forms_info &&
          Array.isArray(data.sub_forms_info)
        ) {
          let formSectionsData = {};
          if (data.filled_form?.value) {
            try {
              formSectionsData = JSON.parse(data.filled_form.value);
            } catch (_e) {
              formSectionsData = {};
            }
          }

          // API returns the full set of subforms for the submission form; do not limit tabs to
          // keys that happen to exist in the saved JSON (empty sections are valid).
          let matchedSubforms = data.sub_forms_info.slice();

          // Use the order array to sort subforms correctly if available
          if (data.order && Array.isArray(data.order)) {
            // Create a map of SubFormId to order value for quick lookup
            const orderMap = {};
            data.order.forEach((orderItem) => {
              orderMap[orderItem.SubFormId] = orderItem.order;
            });

            // Sort subforms based on the order array and add the order property
            matchedSubforms = matchedSubforms
              .map((subform) => ({
                ...subform,
                order:
                  orderMap[subform.id] !== undefined
                    ? orderMap[subform.id] + 1
                    : 999, // +1 to make it 1-based, fallback to 999 for missing order
              }))
              .sort((a, b) => a.order - b.order);
          } else {
            // Fallback to original behavior if no order array is provided
            matchedSubforms = matchedSubforms
              .sort((a, b) => a.id - b.id)
              .map((subform, index) => ({
                ...subform,
                order: index + 1,
              }));
          }

          if (matchedSubforms.length > 0) {
            subformsToSet = matchedSubforms;

            const mergedSections = { ...formSectionsData };
            matchedSubforms.forEach((sf) => {
              const k = String(sf.id);
              if (mergedSections[k] == null) {
                mergedSections[k] = {};
              }
            });
            dispatch({
              type: FORM_ACTIONS.SET_FORM_DATA,
              formType: 'formSections',
              data: mergedSections,
            });

            dispatch({
              type: FORM_ACTIONS.SET_SUB_FORMS,
              subForms: matchedSubforms,
            });

            setTimeout(() => {
              performInitializationIfNeeded(matchedSubforms);
            }, FORM_TIMING.SYNC_DELAY_MS);
          }
        } else if (!subformsToSet && data.filled_form?.value) {
          try {
            const formSectionsData = JSON.parse(data.filled_form.value);
            dispatch({
              type: FORM_ACTIONS.SET_FORM_DATA,
              formType: 'formSections',
              data: formSectionsData,
            });
            const subformIds = Object.keys(formSectionsData);

            if (subformIds.length > 0) {
              const fallbackSubforms = subformIds.map((id, index) => ({
                id: parseInt(id),
                name: `Form ${index + 1}`,
                json_object: '[]',
                order: index + 1,
              }));

              subformsToSet = fallbackSubforms;

              dispatch({
                type: FORM_ACTIONS.SET_SUB_FORMS,
                subForms: fallbackSubforms,
              });

              setTimeout(() => {
                performInitializationIfNeeded(fallbackSubforms);
              }, FORM_TIMING.SYNC_DELAY_MS);
            }
          } catch (parseError) {
            // Silent error handling
          }
        }
      } catch (error) {
        dispatch({
          type: FORM_ACTIONS.SET_API_DATA_LOADED,
          loaded: false,
        });
        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: false,
        });
      }
    },
    [state.isApiDataLoaded, performInitializationIfNeeded]
  );

  useEffect(() => {
    // Clean up any conflicting storage data from the opposite mode
    cleanupConflictingStorage();

    if (isSendbackMode) {
      dispatch({
        type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
        hasData: false,
      });
      return;
    }

    try {
      let savedData = null;

      // ONLY load draft data if draftId is explicitly provided in URL
      // This ensures new requests start clean
      if (draftId) {
        const draftData = getDraftById(draftId);
        if (draftData) {
          savedData = JSON.stringify(draftData);
        }
      }

      if (savedData) {
        const parsed = JSON.parse(savedData);

        if (
          parsed.timestamp &&
          Date.now() - parsed.timestamp < FORM_TIMING.STORAGE_EXPIRY_MS
        ) {
          const dataToHydrate = {
            formSections: parsed.formSections || initialState.formSections,
            hcpLicensesList:
              parsed.hcpLicensesList || initialState.hcpLicensesList,
            additionalDocuments:
              parsed.additionalDocuments || initialState.additionalDocuments,
          };

          const hasSignificantFormData = Object.keys(
            dataToHydrate.formSections
          ).some((formId) => {
            const formData = dataToHydrate.formSections[formId];
            return (
              formData &&
              Object.values(formData).some((val) => val && val.trim() !== '')
            );
          });

          const hasSignificantHcp = Object.values(
            dataToHydrate.hcpLicensesList || {}
          ).some((val) => val && val !== '');

          const hasSignificantAdditionalDocs = (
            dataToHydrate.additionalDocuments || []
          ).some(
            (d) =>
              (d.comment && String(d.comment).trim() !== '') ||
              d.additional_file_id ||
              d.additional_file?.id ||
              d.fileId
          );

          const hasSignificantData =
            hasSignificantFormData ||
            hasSignificantHcp ||
            hasSignificantAdditionalDocs;

          dispatch({
            type: FORM_ACTIONS.HYDRATE_FROM_STORAGE,
            data: dataToHydrate,
          });

          dispatch({
            type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
            hasData: hasSignificantData,
          });

          setTimeout(() => {
            if (window.populateJQueryFromReactState) {
              window.populateJQueryFromReactState();
            }

            setTimeout(() => {
              if (window.forceRenderAllForms) {
                window.forceRenderAllForms();
              }
            }, FORM_TIMING.RENDER_DELAY_MS);
          }, FORM_TIMING.HYDRATION_DELAY_MS);
        } else {
          dispatch({
            type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
            hasData: false,
          });
        }
      } else {
        dispatch({
          type: FORM_ACTIONS.SET_HYDRATION_COMPLETE,
          hasData: false,
        });
      }
    } catch (error) {
      // Silent error handling
    }
  }, [
    storageKey,
    requestId,
    requestProgressId,
    workflowVersionId,
    isSendbackMode,
    modePrefix,
    dynamicId,
    cleanupConflictingStorage,
    draftId,
  ]);

  const setFormData = useCallback((formType, data) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_DATA,
      formType,
      data,
    });
  }, []);

  const setFormErrors = useCallback((formType, errors) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_ERRORS,
      formType,
      errors,
    });
  }, []);

  const setFormLoading = useCallback((formType, loading) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_LOADING,
      formType,
      loading,
    });
  }, []);

  const setFormValidity = useCallback((formType, valid) => {
    dispatch({
      type: FORM_ACTIONS.SET_FORM_VALIDITY,
      formType,
      valid,
    });
  }, []);

  const clearFormErrors = useCallback((formType) => {
    dispatch({
      type: FORM_ACTIONS.CLEAR_FORM_ERRORS,
      formType,
    });
  }, []);

  const setSubForms = useCallback((subForms) => {
    dispatch({
      type: FORM_ACTIONS.SET_SUB_FORMS,
      subForms,
    });
  }, []);

  const setRequest = useCallback((request) => {
    dispatch({
      type: FORM_ACTIONS.SET_REQUEST,
      request,
    });
  }, []);

  const setFilledFormData = useCallback((filledForm) => {
    dispatch({
      type: FORM_ACTIONS.SET_FILLED_FORM,
      filledForm,
    });
  }, []);

  const setActiveSubFormTab = useCallback((activeSubFormTab) => {
    dispatch({
      type: FORM_ACTIONS.SET_ACTIVE_SUB_FORM_TAB,
      activeSubFormTab,
    });
  }, []);

  const clearFieldError = useCallback(
    (subformId, fieldName) => {
      const currentErrors = state.errors.formSections;

      if (currentErrors?.subforms?.[subformId]?.fields?.[fieldName]) {
        const updatedErrors = {
          ...currentErrors,
          subforms: {
            ...currentErrors.subforms,
            [subformId]: {
              ...currentErrors.subforms[subformId],
              fields: {
                ...currentErrors.subforms[subformId].fields,
              },
            },
          },
        };

        delete updatedErrors.subforms[subformId].fields[fieldName];

        const remainingFieldErrors = Object.keys(
          updatedErrors.subforms[subformId].fields
        );
        updatedErrors.subforms[subformId].hasErrors =
          remainingFieldErrors.length > 0;

        if (!updatedErrors.subforms[subformId].hasErrors) {
          delete updatedErrors.subforms[subformId];
        }

        const hasAnyErrors = Object.keys(updatedErrors.subforms).length > 0;
        updatedErrors.hasErrors = hasAnyErrors;

        if (hasAnyErrors) {
          const errorSummary = [];
          Object.keys(updatedErrors.subforms).forEach((subformId) => {
            const subform = updatedErrors.subforms[subformId];
            const fieldCount = Object.keys(subform.fields).length;
            errorSummary.push(
              `${fieldCount} field${fieldCount > 1 ? 's' : ''} in ${
                subform.name
              }`
            );
          });
          updatedErrors.general = `Missing required fields: ${errorSummary.join(
            ', '
          )}`;
        } else {
          updatedErrors.general = null;
        }

        setFormErrors('formSections', updatedErrors);
      }
    },
    [state.errors.formSections, setFormErrors]
  );

  const addFieldError = useCallback(
    (subformId, fieldName, fieldError) => {
      const currentErrors = state.errors.formSections || {
        subforms: {},
        hasErrors: false,
        general: null,
      };

      const subForm = state.subForms.find((sf) => sf.id === subformId);
      const subformName = subForm ? subForm.name : subformId;

      const updatedErrors = {
        ...currentErrors,
        subforms: {
          ...currentErrors.subforms,
          [subformId]: {
            name: subformName,
            hasErrors: true,
            fields: {
              ...(currentErrors.subforms?.[subformId]?.fields || {}),
              [fieldName]: fieldError,
            },
          },
        },
        hasErrors: true,
      };

      const errorSummary = [];
      Object.keys(updatedErrors.subforms).forEach((subformId) => {
        const subform = updatedErrors.subforms[subformId];
        const fieldCount = Object.keys(subform.fields).length;
        errorSummary.push(
          `${fieldCount} field${fieldCount > 1 ? 's' : ''} in ${subform.name}`
        );
      });
      updatedErrors.general = `Missing required fields: ${errorSummary.join(
        ', '
      )}`;

      setFormErrors('formSections', updatedErrors);
    },
    [state.errors.formSections, state.subForms, setFormErrors]
  );

  const clearGenericFieldError = useCallback(
    (formType, fieldName) => {
      const currentErrors = state.errors[formType];

      if (currentErrors?.fields?.[fieldName]) {
        const updatedErrors = {
          ...currentErrors,
          fields: {
            ...currentErrors.fields,
          },
        };

        delete updatedErrors.fields[fieldName];

        const remainingFieldErrors = Object.keys(updatedErrors.fields);
        updatedErrors.hasErrors = remainingFieldErrors.length > 0;

        if (updatedErrors.hasErrors) {
          const errorMessages = remainingFieldErrors.map(
            (fname) => updatedErrors.fields[fname].label
          );
          updatedErrors.general = `Missing or invalid fields: ${errorMessages.join(
            ', '
          )}`;
        } else {
          updatedErrors.general = null;
        }

        setFormErrors(formType, updatedErrors);
      }
    },
    [state.errors, setFormErrors]
  );

  const addGenericFieldError = useCallback(
    (formType, fieldName, fieldError) => {
      const currentErrors = state.errors[formType] || {
        fields: {},
        hasErrors: false,
        general: null,
      };

      const updatedErrors = {
        ...currentErrors,
        fields: {
          ...currentErrors.fields,
          [fieldName]: fieldError,
        },
        hasErrors: true,
      };

      const errorMessages = Object.keys(updatedErrors.fields).map(
        (fname) => updatedErrors.fields[fname].label
      );
      updatedErrors.general = `Missing or invalid fields: ${errorMessages.join(
        ', '
      )}`;

      setFormErrors(formType, updatedErrors);
    },
    [state.errors, setFormErrors]
  );

  const syncAllTabsData = useCallback(
    async (subForms, currentActiveTab, setActiveTab) => {
      if (!subForms || subForms.length === 0) {
        return {};
      }

      const allFormData = {};
      const originalTab = currentActiveTab;

      for (const subForm of subForms) {
        if (setActiveTab && typeof setActiveTab === 'function') {
          await Promise.resolve(setActiveTab(subForm.id));
          await new Promise((resolve) =>
            setTimeout(resolve, FORM_TIMING.HYDRATION_DELAY_MS)
          );
        }

        const formData = getFormSectionsData();

        if (formData && formData[subForm.id]) {
          allFormData[subForm.id] = formData[subForm.id];
        } else {
          allFormData[subForm.id] = {};
        }
      }

      if (
        setActiveTab &&
        typeof setActiveTab === 'function' &&
        originalTab != null &&
        originalTab !== ''
      ) {
        await Promise.resolve(setActiveTab(originalTab));
        await new Promise((resolve) =>
          setTimeout(resolve, FORM_TIMING.SYNC_DELAY_MS)
        );
      }

      return allFormData;
    },
    [getFormSectionsData]
  );

  const syncFormSectionsData = useCallback(
    async (subForms = null, activeTab = null, setActiveTab = null) => {
      if (subForms && Array.isArray(subForms) && subForms.length > 1) {
        const allFormData = await syncAllTabsData(
          subForms,
          activeTab,
          setActiveTab
        );

        if (Object.keys(allFormData).length > 0) {
          const mergedData = {
            ...state.formSections,
            ...allFormData,
          };

          setFormData('formSections', mergedData);

          return mergedData;
        }
      }

      const queries = [
        'input[name^="text-"], textarea[name^="textarea-"]',
        'input[type="text"], textarea',
        '.form-control',
        'input, textarea, select',
        '[data-subform] input, [data-subform] textarea',
      ];

      let allInputs = [];
      queries.forEach((query, index) => {
        const inputs = document.querySelectorAll(query);
        allInputs = [...allInputs, ...Array.from(inputs)];
      });

      const uniqueInputs = [...new Set(allInputs)];

      await new Promise((resolve) =>
        setTimeout(resolve, FORM_TIMING.SYNC_DELAY_MS)
      );

      const jqueryData = getFormSectionsData();

      const domHasData = uniqueInputs.some(
        (input) => input.value && input.value.trim() !== ''
      );
      const jqueryHasData = Object.values(jqueryData).some((formData) =>
        Object.values(formData || {}).some(
          (value) => value && value.trim() !== ''
        )
      );

      if (domHasData && !jqueryHasData) {
        return state.formSections;
      }

      if (Object.keys(jqueryData).length > 0) {
        const mergedData = {
          ...state.formSections,
          ...jqueryData,
        };

        setFormData('formSections', mergedData);

        return mergedData;
      } else {
        return state.formSections;
      }
    },
    [getFormSectionsData, setFormData, state.formSections, syncAllTabsData]
  );

  const validateHcpLicensesList = useCallback((data) => {
    const errors = {
      fields: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    /** Unique human-readable messages for the summary banner (not field labels). */
    const errorMessages = [];
    const addErr = (name, label, msg) => {
      errors.fields[name] = { label, error: msg, required: true };
      totalErrorCount++;
      const summaryPiece = String(
        msg != null ? msg : `${label} is required`
      ).trim();
      if (summaryPiece && !errorMessages.includes(summaryPiece)) {
        errorMessages.push(summaryPiece);
      }
    };

    const required = [
      ['hcp_name', 'HCP Name'],
      ['hcp_emirates_id', 'HCP Emirates ID'],
      ['hcp_email', 'HCP Email'],
      ['hcp_phone', 'HCP Phone'],
      ['license_number', 'License #'],
      ['license_issue_date', 'License issue date'],
      ['license_end_date', 'License renewal/end date'],
    ];
    required.forEach(([name, label]) => {
      const v = data[name];
      if (v === undefined || v === null || v === '') {
        addErr(name, label, `${label} is required`);
      }
    });

    const eid = String(data.hcp_emirates_id || '').replace(/\D/g, '');
    if (eid && !/^\d{15}$/.test(eid)) {
      addErr('hcp_emirates_id', 'HCP Emirates ID', 'Must be 15 digits');
    }

    if (data.hcp_email && !VALIDATION.EMAIL_REGEX.test(data.hcp_email)) {
      addErr('hcp_email', 'HCP Email', 'Invalid email');
    }

    const lic = data.license_image_file;
    const hasLicenseFile =
      Array.isArray(lic) &&
      lic.length > 0 &&
      (lic[0]?.id || lic[0]?.original_name);
    if (!hasLicenseFile) {
      addErr(
        'license_image_file',
        'License image',
        'License image is required'
      );
    }

    const niIssue = toCalendarDayMs(data.license_issue_date);
    const neEnd = toCalendarDayMs(data.license_end_date);
    if (niIssue != null && neEnd != null && niIssue >= neEnd) {
      addErr(
        'license_end_date',
        'License renewal/end date',
        i18n.t('HcpLicenseEndAfterIssue')
      );
    }

    const bi = toCalendarDayMs(data.renewalBaselineIssueDate);
    const be = toCalendarDayMs(data.renewalBaselineEndDate);
    if (bi != null && be != null) {
      const ni = toCalendarDayMs(data.license_issue_date);
      const ne = toCalendarDayMs(data.license_end_date);
      if (ni != null && (ni <= be || ni <= bi)) {
        addErr(
          'license_issue_date',
          'License issue date',
          i18n.t('renewalNewIssueDateRule')
        );
      }
      if (ne != null && (ne <= be || ne <= bi)) {
        addErr(
          'license_end_date',
          'License renewal/end date',
          i18n.t('renewalNewEndDateRule')
        );
      }
    }

    if (
      data.total_cme_hours !== '' &&
      data.total_cme_hours != null &&
      (isNaN(Number(data.total_cme_hours)) || Number(data.total_cme_hours) < 0)
    ) {
      addErr('total_cme_hours', 'Total CME hours', 'Must be a number ≥ 0');
    }

    errors.hasErrors = totalErrorCount > 0;
    if (errors.hasErrors) {
      errors.general = errorMessages.join('\n\n');
    }
    return { errors, isValid: !errors.hasErrors };
  }, []);

  // ref to track validation timeouts for debouncing
  const validationTimeoutRef = useRef({});

  const setFormDataWithValidation = useCallback(
    (formType, data) => {
      let dispatchPayload = data;
      if (formType === 'hcpLicensesList') {
        dispatchPayload = {
          ...hcpLicensesListMergeRef.current,
          ...data,
        };
        hcpLicensesListMergeRef.current = dispatchPayload;
      }

      dispatch({
        type: FORM_ACTIONS.SET_FORM_DATA,
        formType,
        data: dispatchPayload,
      });

      // clear any existing validation timeout for this form type
      if (validationTimeoutRef.current[formType]) {
        clearTimeout(validationTimeoutRef.current[formType]);
      }

      // debounce validation to reduce re-renders - batch errors and validity together
      validationTimeoutRef.current[formType] = setTimeout(() => {
        let validationResult = null;

        switch (formType) {
          case 'hcpLicensesList':
            validationResult = validateHcpLicensesList(dispatchPayload);
            break;
          default:
            return;
        }

        if (validationResult) {
          // batch both updates into single setTimeout to reduce re-renders
          dispatch({
            type: FORM_ACTIONS.SET_FORM_ERRORS,
            formType,
            errors: validationResult.errors,
          });

          dispatch({
            type: FORM_ACTIONS.SET_FORM_VALIDITY,
            formType,
            valid: validationResult.isValid,
          });
        }

        delete validationTimeoutRef.current[formType];
      }, 100); // increased from 0ms to 100ms to batch rapid changes
    },
    [validateHcpLicensesList]
  );

  const validateFormSections = useCallback((data, subForms) => {
    const errors = {
      subforms: {},
      hasErrors: false,
      general: null,
    };

    let totalErrorCount = 0;
    const errorSummary = [];

    for (const subForm of subForms || []) {
      const formData = data[subForm.id] || {};
      const subformErrors = {
        name: subForm.name,
        hasErrors: false,
        fields: {},
      };

      let formFields = [];

      try {
        formFields = JSON.parse(subForm.json_object);
      } catch (e) {
        continue;
      }

      if (Array.isArray(formFields)) {
        for (const field of formFields) {
          if (field && field.required) {
            const fieldValue = formData[field.name];
            const isEmpty = !fieldValue || fieldValue === '';

            if (isEmpty) {
              const cleanedLabel = cleanLabel(field.label || field.name);
              subformErrors.hasErrors = true;
              subformErrors.fields[field.name] = {
                label: cleanedLabel,
                error: `${cleanedLabel} is required`,
                required: true,
              };
              totalErrorCount++;
            }
          }
        }
      }

      if (subformErrors.hasErrors) {
        errors.subforms[subForm.id] = subformErrors;
        const fieldCount = Object.keys(subformErrors.fields).length;
        errorSummary.push(
          `${fieldCount} field${fieldCount > 1 ? 's' : ''} in ${subForm.name}`
        );
      }
    }

    errors.hasErrors = totalErrorCount > 0;

    if (errors.hasErrors) {
      errors.general = `Missing required fields: ${errorSummary.join(', ')}`;
    }

    return { errors, isValid: !errors.hasErrors };
  }, []);

  const submitAllForms = useCallback(async () => {
    const subForms = state.subForms;

    dispatch({
      type: FORM_ACTIONS.SET_SUBMISSION_STATE,
      state: { isSubmitting: true, error: null, success: false },
    });

    try {
      const formSectionsData = trimStringValues(state.formSections);
      const hcpList = state.hcpLicensesList;
      const hcpLicensesListData = {
        hcp_name:
          typeof hcpList.hcp_name === 'string'
            ? hcpList.hcp_name.trim()
            : hcpList.hcp_name,
        hcp_emirates_id: hcpList.hcp_emirates_id
          ? String(hcpList.hcp_emirates_id).replace(/\D/g, '')
          : '',
        hcp_email:
          typeof hcpList.hcp_email === 'string'
            ? hcpList.hcp_email.trim()
            : hcpList.hcp_email,
        hcp_phone: hcpList.hcp_phone,
        license_number:
          typeof hcpList.license_number === 'string'
            ? hcpList.license_number.trim()
            : hcpList.license_number,
        license_issue_date: hcpList.license_issue_date,
        license_end_date: hcpList.license_end_date,
        license_image_file: hcpList.license_image_file,
        additional_attachments: hcpList.additional_attachments,
        total_cme_hours: hcpList.total_cme_hours,
        additional_notification_dates: '',
      };

      const formSectionsValidation = validateFormSections(
        formSectionsData,
        subForms
      );

      // validate hcpLicensesList if enabled
      let hcpLicensesListValidation = { isValid: true, errors: {} };
      if (
        state.hasHcpLicensesList &&
        hcpLicensesListData &&
        Object.keys(hcpLicensesListData).length > 0
      ) {
        hcpLicensesListValidation = validateHcpLicensesList({
          ...hcpList,
          ...hcpLicensesListData,
        });
        setFormValidity('hcpLicensesList', hcpLicensesListValidation.isValid);
        setFormErrors('hcpLicensesList', hcpLicensesListValidation.errors);
      }

      setFormValidity('formSections', formSectionsValidation.isValid);

      setFormErrors('formSections', formSectionsValidation.errors);

      const allValid =
        formSectionsValidation.isValid && hcpLicensesListValidation.isValid;

      if (!allValid) {
        throw new Error('Please fix validation errors before submitting');
      }

      if (
        !isSendbackMode &&
        linkedStudiesRequired &&
        (!linkedRequestIds || linkedRequestIds.length === 0)
      ) {
        const msg = i18n.t('linkedStudiesRequired');
        toast.error(msg, { position: 'top-center' });
        dispatch({
          type: FORM_ACTIONS.SET_SUBMISSION_STATE,
          state: {
            isSubmitting: false,
            success: false,
            error: msg,
            data: null,
          },
        });
        throw new Error(msg);
      }

      const currentStageId = state.request?.Stages?.[0]?.id || initialStageId;

      const requestBody = {
        stage_id: currentStageId,
        wf_version_id: workflowVersionId,
        study_name: studyName?.trim() || studyName,
        request_progress_id: null,
        formSections: JSON.stringify(formSectionsData),
        hcpLicensesList: state.hasHcpLicensesList
          ? hcpLicensesListData
          : undefined,
        additionalDocuments:
          state.additionalDocuments.length > 0
            ? state.additionalDocuments.map((doc) => ({
                comment: doc.comment,
                fileId:
                  doc.additional_file?.id ||
                  doc.additional_file_id ||
                  doc.fileId ||
                  null,
              }))
            : undefined,
        ...(!isSendbackMode && linkedRequestIds?.length > 0
          ? { linked_request_ids: linkedRequestIds }
          : {}),
      };

      if (isSendbackMode) {
        try {
          const filledFormId = state.filledForm?.id;
          if (!filledFormId) {
            throw new Error('No filled form ID found for update');
          }

          const updateRequestBody = {
            formSections: JSON.stringify(formSectionsData),
            study_name: studyName?.trim() || studyName,
            hcpLicensesList: state.hasHcpLicensesList
              ? hcpLicensesListData
              : undefined,
          };

          const response = await axiosInstance.put(
            `/api/endUserFilledForms/${filledFormId}`,
            updateRequestBody,
            {
              withCredentials: true,
              suppressErrorToast: true, // Handle error toast manually for better UX
            }
          );

          dispatch({
            type: FORM_ACTIONS.SET_SUBMISSION_STATE,
            state: {
              isSubmitting: false,
              success: true,
              error: null,
              data: {
                mode: 'sendback_update',
                response: response.data,
                updateRequestBody,
                filledFormId,
              },
            },
          });

          toast.success(`"${requestBody.study_name}" updated successfully!`, {
            position: 'top-center',
            autoClose: 5000,
          });

          localStorage.removeItem(storageKey);

          return true;
        } catch (error) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            'Failed to update form';

          dispatch({
            type: FORM_ACTIONS.SET_SUBMISSION_STATE,
            state: {
              isSubmitting: false,
              success: false,
              error: errorMessage,
              data: null,
            },
          });

          toast.error(errorMessage, {
            position: 'top-center',
            autoClose: 8000,
          });

          return false;
        }
      }

      const mainFormResponse = await axiosInstance.post(
        '/api/endUserFilledForms',
        requestBody,
        {
          suppressErrorToast: true, // Handle error toast manually for better UX
        }
      );

      dispatch({
        type: FORM_ACTIONS.SET_SUBMISSION_STATE,
        state: {
          isSubmitting: false,
          success: true,
          error: null,
          data: {
            mode: 'normal_submit',
            endUserFilledForm: mainFormResponse.data,
            requestBody: requestBody,
          },
        },
      });

      toast.success('All forms submitted successfully!', {
        position: 'top-center',
      });

      // Remove draft from localStorage if it was loaded from a draft
      if (draftId) {
        localStorage.removeItem(draftId);
      }
      localStorage.removeItem(storageKey);

      // Return the response data which contains request_id
      return mainFormResponse.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Submission failed';

      dispatch({
        type: FORM_ACTIONS.SET_SUBMISSION_STATE,
        state: {
          isSubmitting: false,
          success: false,
          error: errorMessage,
        },
      });

      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 8000,
      });

      return false;
    }
  }, [
    state.formSections,
    state.subForms,
    state.request,
    state.filledForm,
    state.hasHcpLicensesList,
    state.hcpLicensesList,
    state.additionalDocuments,
    validateFormSections,
    validateHcpLicensesList,
    setFormValidity,
    setFormErrors,
    initialStageId,
    workflowVersionId,
    studyName,
    storageKey,
    isSendbackMode,
    draftId,
    linkedStudiesRequired,
    linkedRequestIds,
  ]);

  const validateForms = useCallback(
    async (formsTypes = []) => {
      for (const formType of formsTypes) {
        switch (formType) {
          case FORM_TYPES.FORM_SECTIONS:
            const formSectionsValidation = validateFormSections(
              state.formSections,
              state.subForms
            );
            setFormValidity('formSections', formSectionsValidation.isValid);
            setFormErrors('formSections', formSectionsValidation.errors);
            break;

          case FORM_TYPES.HCP_LICENSES_LIST:
            // validate hcpLicensesList if enabled
            const hcpLicensesListData = state.hcpLicensesList;
            let hcpLicensesListValidation = { isValid: true, errors: {} };
            if (
              state.hasHcpLicensesList &&
              hcpLicensesListData &&
              Object.keys(hcpLicensesListData).length > 0
            ) {
              hcpLicensesListValidation =
                validateHcpLicensesList(hcpLicensesListData);
              setFormValidity(
                'hcpLicensesList',
                hcpLicensesListValidation.isValid
              );
              setFormErrors(
                'hcpLicensesList',
                hcpLicensesListValidation.errors
              );
            }
            break;

          default:
            break;
        }
      }
    },
    [
      state.formSections,
      state.subForms,
      state.hasHcpLicensesList,
      state.hcpLicensesList,
      validateFormSections,
      validateHcpLicensesList,
      setFormValidity,
      setFormErrors,
    ]
  );

  const resetForms = useCallback(() => {
    dispatch({ type: FORM_ACTIONS.RESET_FORMS });
    localStorage.removeItem(storageKey);
    formSectionsDataRef.current = {};
  }, [storageKey]);

  useEffect(() => {
    if (!state.isApiDataLoaded) {
      if (isSendbackMode && requestProgressId) {
        fetchSendbackDataAndInitialize(requestProgressId);
      } else if (workflowVersionId) {
        fetchWorkflowDataAndInitialize(workflowVersionId);
      }
    }
  }, [
    workflowVersionId,
    requestProgressId,
    isSendbackMode,
    state.isApiDataLoaded,
    fetchWorkflowDataAndInitialize,
    fetchSendbackDataAndInitialize,
  ]);

  useEffect(() => {
    if (
      state.isHydrationComplete &&
      state.isApiDataLoaded &&
      !state.isInitializationComplete
    ) {
      performInitializationIfNeeded(state.subForms);
    }
  }, [
    state.isHydrationComplete,
    state.isApiDataLoaded,
    state.isInitializationComplete,
    state.subForms,
    performInitializationIfNeeded,
  ]);

  /** One-time defaults: profile first tab or HCP license tab (do not depend on activeSubFormTab — avoids effect churn on every vertical click). */
  useEffect(() => {
    if (!state.isApiDataLoaded) {
      return;
    }

    if (state.attachEndUserProfileSnapshot) {
      if (!hcpProfileMenuDefaultAppliedRef.current) {
        hcpProfileMenuDefaultAppliedRef.current = true;
        setActiveSubFormTab(HCP_PROFILE_REQUEST_TAB_IDS.personal);
      }
      return;
    }

    if (state.hasHcpLicensesList && !hcpVerticalTabDefaultAppliedRef.current) {
      hcpVerticalTabDefaultAppliedRef.current = true;
      setActiveSubFormTab('custom_hcp_licenses_list');
    }
  }, [
    state.isApiDataLoaded,
    state.attachEndUserProfileSnapshot,
    state.hasHcpLicensesList,
    setActiveSubFormTab,
  ]);

  /** First workflow subform when no profile/HCP license tab took over and tab is still empty. */
  useEffect(() => {
    if (!state.isApiDataLoaded) {
      return;
    }
    if (state.attachEndUserProfileSnapshot || state.hasHcpLicensesList) {
      return;
    }
    if (state.activeSubFormTab != null && state.activeSubFormTab !== '') {
      return;
    }
    if (state.subForms.length > 0) {
      setActiveSubFormTab(state.subForms[0].id);
    }
  }, [
    state.isApiDataLoaded,
    state.attachEndUserProfileSnapshot,
    state.hasHcpLicensesList,
    state.activeSubFormTab,
    state.subForms,
    setActiveSubFormTab,
  ]);

  // ref to track if initial validation has run
  const initialValidationRunRef = useRef(false);

  // optimized validation effect - only runs on hydration, not on every state change
  useEffect(() => {
    // only run initial validation once after hydration
    if (
      state.isHydrationComplete &&
      state.hasHydratedData &&
      !initialValidationRunRef.current
    ) {
      initialValidationRunRef.current = true;

      setTimeout(() => {
        try {
          // batch all validation checks and only dispatch if changes detected
          const updates = [];

          if (
            state.subForms &&
            state.subForms.length > 0 &&
            validateFormSections
          ) {
            const formSectionsValidation = validateFormSections(
              state.formSections,
              state.subForms
            );
            const currentFormSectionsErrors = state.errors.formSections;
            const currentFormSectionsValidity = state.validity.formSections;

            const formSectionsErrorsChanged =
              JSON.stringify(currentFormSectionsErrors) !==
              JSON.stringify(formSectionsValidation.errors);
            const formSectionsValidityChanged =
              currentFormSectionsValidity !== formSectionsValidation.isValid;

            if (formSectionsErrorsChanged || formSectionsValidityChanged) {
              if (formSectionsErrorsChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_ERRORS,
                  formType: 'formSections',
                  errors: formSectionsValidation.errors,
                });
              }
              if (formSectionsValidityChanged) {
                updates.push({
                  type: FORM_ACTIONS.SET_FORM_VALIDITY,
                  formType: 'formSections',
                  valid: formSectionsValidation.isValid,
                });
              }
            }
          }

          // batch dispatch all updates
          updates.forEach((update) => dispatch(update));
        } catch (error) {
          // silent error handling
        }
      }, FORM_TIMING.SYNC_DELAY_MS);
    }
  }, [
    state.isHydrationComplete,
    state.hasHydratedData,
    state.formSections,
    state.subForms,
    state.errors.formSections,
    state.validity.formSections,
    validateFormSections,
  ]);

  useEffect(() => {
    window.clearFieldError = clearFieldError;
    window.addFieldError = addFieldError;
    window.clearGenericFieldError = clearGenericFieldError;
    window.addGenericFieldError = addGenericFieldError;

    window.getCurrentSubFormDefinition = (subformId) => {
      return state.subForms.find((sf) => sf.id === subformId);
    };

    window.getValidationFunctions = () => ({
      validateFormSections,
    });

    return () => {
      delete window.clearFieldError;
      delete window.addFieldError;
      delete window.clearGenericFieldError;
      delete window.addGenericFieldError;
      delete window.getCurrentSubFormDefinition;
      delete window.getValidationFunctions;
    };
  }, [
    clearFieldError,
    addFieldError,
    clearGenericFieldError,
    addGenericFieldError,
    state.subForms,
    validateFormSections,
  ]);

  const saveAsDraft = useCallback(
    async (
      draftName,
      subForms = null,
      activeTab = null,
      setActiveTab = null
    ) => {
      try {
        const syncedData = await syncFormSectionsData(
          subForms || state.subForms,
          activeTab,
          setActiveTab
        );

        await new Promise((resolve) =>
          setTimeout(resolve, FORM_TIMING.HYDRATION_DELAY_MS)
        );

        const timestamp = Date.now();
        const draftId = `${STORAGE.MULTI_FORM_PREFIX}newRequest_${workflowVersionId}_${timestamp}`;

        const dataToSave = {
          draftId,
          draftName:
            draftName || `Draft ${new Date(timestamp).toLocaleString('en-GB')}`,
          workflowVersionId,
          formName: state.workflowName || state.formName,
          formSections: syncedData || state.formSections,
          hcpLicensesList: state.hcpLicensesList,
          additionalDocuments: state.additionalDocuments,
          timestamp,
        };

        const hasMeaningfulData = Object.keys(
          dataToSave.formSections || {}
        ).some((formId) =>
          Object.values(dataToSave.formSections[formId] || {}).some(
            (val) => val && val.trim() !== ''
          )
        );

        const hasHcpDraftData = Object.values(
          dataToSave.hcpLicensesList || {}
        ).some((val) => val && val !== '');

        const hasAdditionalDocsDraftData = (
          dataToSave.additionalDocuments || []
        ).some(
          (d) =>
            (d.comment && String(d.comment).trim() !== '') ||
            d.additional_file_id ||
            d.additional_file?.id ||
            d.fileId
        );

        if (
          hasMeaningfulData ||
          hasHcpDraftData ||
          hasAdditionalDocsDraftData
        ) {
          localStorage.setItem(draftId, JSON.stringify(dataToSave));
        }

        return { success: true, message: 'Draft saved successfully!', draftId };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [
      syncFormSectionsData,
      state.formSections,
      state.hcpLicensesList,
      state.additionalDocuments,
      state.subForms,
      state.workflowName,
      state.formName,
      workflowVersionId,
    ]
  );

  const addAdditionalDocument = useCallback((document) => {
    const newDoc = {
      ...document,
      id:
        document.id ||
        `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      isOwner: true,
      isTemp: true,
    };
    dispatch({
      type: FORM_ACTIONS.ADD_ADDITIONAL_DOCUMENT,
      document: newDoc,
    });
    return newDoc;
  }, []);

  const updateAdditionalDocument = useCallback((documentId, updates) => {
    dispatch({
      type: FORM_ACTIONS.UPDATE_ADDITIONAL_DOCUMENT,
      documentId,
      updates,
    });
  }, []);

  const deleteAdditionalDocument = useCallback((documentId) => {
    dispatch({
      type: FORM_ACTIONS.DELETE_ADDITIONAL_DOCUMENT,
      documentId,
    });
  }, []);

  const setAdditionalDocuments = useCallback((documents) => {
    dispatch({
      type: FORM_ACTIONS.SET_ADDITIONAL_DOCUMENTS,
      documents,
    });
  }, []);

  const contextValue = {
    ...state,

    hcpLicenseRenewalLockIdentity: coalesceHcpListEnabled(
      state.request?.license_renewal
    ),

    setFormData,
    setFormDataWithValidation,
    setFormErrors,
    setFormLoading,
    setFormValidity,

    setFormSectionsDataRef,
    getFormSectionsData,
    syncFormSectionsData,
    syncAllTabsData,

    validateFormSections,
    validateHcpLicensesList,
    validateForms,

    submitAllForms,
    resetForms,
    saveAsDraft,

    clearFormErrors,
    fetchWorkflowDataAndInitialize,
    performInitializationIfNeeded,

    setSubForms,
    setRequest,
    setFilledFormData,

    setActiveSubFormTab,

    clearFieldError,
    addFieldError,
    clearGenericFieldError,
    addGenericFieldError,

    addAdditionalDocument,
    updateAdditionalDocument,
    deleteAdditionalDocument,
    setAdditionalDocuments,

    requestId,
    requestProgressId,
    isSendbackMode,
    workflowVersionId,

    linkedStudiesEnabled,
    linkedRequestIds,
  };

  return (
    <MultiFormContext.Provider value={contextValue}>
      {children}
    </MultiFormContext.Provider>
  );
};

export const useMultiForm = () => {
  const context = useContext(MultiFormContext);
  if (!context) {
    throw new Error('useMultiForm must be used within a MultiFormProvider');
  }
  return context;
};

export default MultiFormContext;
